import React, { useState, useEffect, useMemo } from 'react';
import ReportRenderer from './ReportRenderer';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import {
    FileText,
    Calendar,
    Filter,
    Building,
    HeartPulse,
    Search,
    Info,
    Clock,
    Calculator,
    AlertTriangle,
    LogOut,
    ShieldCheck,
    ListChecks,
    CheckCircle2,
    Database,
    RotateCcw,
    UserX,
    TrendingUp,
    CalendarDays,
    ShieldAlert,
    ChevronDown,
    Star,
    BookmarkPlus,
    Trash2,
    Layers,
    Table2,
    Grid
} from 'lucide-react';
import CustomDatePicker from './common/CustomDatePicker';
import DarkAwareSelect from './common/DarkAwareSelect';

// Hızlı erişim butonlarında sabit görünecek 5 ana PDKS raporu (sırasıyla)
const QUICK_REPORT_KEYS = [
    'puantaj_raporu',
    'latecomers_report',
    'early_leavers_report',
    'absenteeism_report',
    'raw_transitions'
];

const QUICK_BUTTON_LABELS = {
    puantaj_raporu: 'Puantaj Raporu',
    latecomers_report: 'Geç Gelenler',
    early_leavers_report: 'Erken Çıkanlar',
    absenteeism_report: 'Gelmeyenler',
    raw_transitions: 'Ham Geçiş Raporu'
};

const FALLBACK_REPORTS = [
    {
        reportType: 'puantaj_raporu',
        permissionKey: 'reports:view_puantaj_raporu',
        title: 'Puantaj Raporu',
        category: 'PDKS Raporları',
        description: 'Günlük vardiya, giriş/çıkış, net çalışma, eksik süre, fazla mesai (Normal/HT/RT) ve izin sürelerini içeren detaylı puantaj cetveli.',
        requiresDateRange: true
    },
    {
        reportType: 'latecomers_report',
        permissionKey: 'reports:view_latecomers_report',
        title: 'Geç Gelenler Raporu',
        category: 'PDKS Raporları',
        description: 'Vardiya giriş saatinden sonra gelen personellerin geç kaldıkları tarihleri, toplam sayıyı ve toplam geç kalma süresini özetler.',
        requiresDateRange: true
    },
    {
        reportType: 'early_leavers_report',
        permissionKey: 'reports:view_early_leavers_report',
        title: 'Erken Çıkanlar Raporu',
        category: 'PDKS Raporları',
        description: 'Vardiya bitiş saatinden önce ayrılan personellerin erken çıktıkları günleri ve toplam sürelerini özetler.',
        requiresDateRange: true
    },
    {
        reportType: 'absenteeism_report',
        permissionKey: 'reports:view_absenteeism_report',
        title: 'Devamsızlık (Gelmeyenler) Raporu',
        category: 'PDKS Raporları',
        description: 'Planlı iş günlerinde giriş-çıkış hareketi ve onaylı izni bulunmayan (mazeretsiz devamsız) personelleri gün bazında listeler.',
        requiresDateRange: true
    },
    {
        reportType: 'raw_transitions',
        permissionKey: 'reports:view_raw_transitions',
        title: 'Ham Geçişler Raporu',
        category: 'PDKS Raporları',
        description: 'Cihaz, Mobil QR ve Manuel kayıtlardan gelen tüm giriş/çıkış hareketlerini saniyesiyle ve cihaz bilgisiyle listeler.',
        requiresDateRange: true
    },
    {
        reportType: 'daily_attendance',
        permissionKey: 'reports:view_daily_attendance',
        title: 'Günlük Devam Raporu',
        category: 'PDKS Raporları',
        description: 'Personellerin gün bazında planlanan vardiyasını, giriş/çıkış saatlerini, net çalışma süresini ve devam durumunu gösterir.',
        requiresDateRange: true
    },
    {
        reportType: 'entry_exit_summary',
        permissionKey: 'reports:view_entry_exit_summary',
        title: 'Giriş-Çıkış Özet Raporu',
        category: 'PDKS Raporları',
        description: 'Turnike ve cihaz geçişlerini gün bazında gruplayarak ilk giriş, son çıkış, hareket adedi ve içeride kalma süresini özetler.',
        requiresDateRange: true
    },
    {
        reportType: 'overtime_report',
        permissionKey: 'reports:view_overtime_report',
        title: 'Fazla Mesai Raporu',
        category: 'PDKS Raporları',
        description: 'Puantaj motoru tarafından hesaplanan Normal Fazla Mesai, Hafta Tatili (HT) ve Resmi Tatil (RT) mesailerini listeler.',
        requiresDateRange: true
    },
    {
        reportType: 'leaves_report',
        permissionKey: 'reports:view_leaves_report',
        title: 'İzin Kullanım Hareketleri Raporu',
        category: 'PDKS Raporları',
        description: 'Seçilen tarih aralığında personellerin kullandığı yıllık izin, mazeret izni, rapor ve ücretsiz izin hareketlerini listeler.',
        requiresDateRange: true
    },
    {
        reportType: 'failed_pass_attempts',
        permissionKey: 'reports:view_failed_pass_attempts',
        title: 'Yetkisiz / Başarısız ve Konum Dışı Geçişler Raporu',
        category: 'PDKS Raporları',
        description: 'Tanımsız kart okutmaları, pasif cihaz denemeleri ve mobil QR/GPS konum dışı (saha dışı) başarısız geçiş denemelerini listeler.',
        requiresDateRange: true,
        supportsViewModes: false
    },
    {
        reportType: 'monthly_attendance_matrix',
        permissionKey: 'reports:view_monthly_attendance_matrix',
        title: 'Aylık Yatay Puantaj / İmza Çizelgesi',
        category: 'PDKS Raporları',
        description: 'Personellerin sol sütunda, seçilen ayın günlerinin (01..31) yan yana sütunlarda yer aldığı; hücrelerde X, FM, HT, RT, Yİ, D kodlarını ve sağda toplam icmal ile imza alanını gösteren yatay çizelge.',
        requiresDateRange: true,
        supportsViewModes: false
    }
];

const VIEW_MODE_SUPPORTED_REPORTS = new Set([
    'puantaj_raporu',
    'latecomers_report',
    'early_leavers_report',
    'absenteeism_report',
    'overtime_report',
    'leaves_report'
]);

const REPORT_STYLE_MAP = {
    puantaj_raporu: { icon: Calculator, iconBg: 'bg-emerald-600 text-white' },
    daily_attendance: { icon: Clock, iconBg: 'bg-teal-600 text-white' },
    entry_exit_summary: { icon: ListChecks, iconBg: 'bg-amber-600 text-white' },
    raw_transitions: { icon: Database, iconBg: 'bg-orange-600 text-white' },
    latecomers_report: { icon: AlertTriangle, iconBg: 'bg-amber-500 text-white' },
    early_leavers_report: { icon: LogOut, iconBg: 'bg-rose-600 text-white' },
    absenteeism_report: { icon: UserX, iconBg: 'bg-red-600 text-white' },
    overtime_report: { icon: TrendingUp, iconBg: 'bg-emerald-600 text-white' },
    leaves_report: { icon: CalendarDays, iconBg: 'bg-teal-600 text-white' },
    failed_pass_attempts: { icon: ShieldAlert, iconBg: 'bg-rose-700 text-white' },
    monthly_attendance_matrix: { icon: Grid, iconBg: 'bg-emerald-700 text-white' },
    legal_consent_report: { icon: ShieldCheck, iconBg: 'bg-emerald-600 text-white' }
};

const ReportsPage = () => {
    const { firmCode, permissions = [] } = useAuth();

    const [catalogReports, setCatalogReports] = useState([]);

    // Filter Options
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [personnel, setPersonnel] = useState([]);
    const [locations, setLocations] = useState([]);
    const [workingRegimes, setWorkingRegimes] = useState([]);

    // Filter States
    const [selectedReport, setSelectedReport] = useState('');
    const [viewMode, setViewMode] = useState('detailed'); // 'detailed' | 'summary'
    const [includeSubtotals, setIncludeSubtotals] = useState(true);
    const [colorizeMatrix, setColorizeMatrix] = useState(true);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [endDate, setEndDate] = useState(() => new Date());
    const [searchText, setSearchText] = useState('');

    // Favori Rapor & Filtre Şablonları
    const presetStorageKey = `pdks_fav_reports_${firmCode || 'default'}`;
    const [savedPresets, setSavedPresets] = useState(() => {
        try {
            const raw = localStorage.getItem(`pdks_fav_reports_${firmCode || 'default'}`);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [showPresetInput, setShowPresetInput] = useState(false);
    const [presetNameInput, setPresetNameInput] = useState('');

    useEffect(() => {
        try {
            const raw = localStorage.getItem(presetStorageKey);
            setSavedPresets(raw ? JSON.parse(raw) : []);
        } catch {
            setSavedPresets([]);
        }
    }, [presetStorageKey]);

    // Multi-select states
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [selectedPersonnel, setSelectedPersonnel] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedWorkingRegimes, setSelectedWorkingRegimes] = useState([]);
    const [selectedGenders, setSelectedGenders] = useState([]);
    const [selectedBloodTypes, setSelectedBloodTypes] = useState([]);

    const [reportRequest, setReportRequest] = useState(null);

    const genderOptions = [
        { label: 'Erkek', value: 'Erkek' },
        { label: 'Kadın', value: 'Kadın' }
    ];

    const bloodTypeOptions = [
        { label: 'A Rh+', value: 'A Rh+' },
        { label: 'A Rh-', value: 'A Rh-' },
        { label: 'B Rh+', value: 'B Rh+' },
        { label: 'B Rh-', value: 'B Rh-' },
        { label: 'AB Rh+', value: 'AB Rh+' },
        { label: 'AB Rh-', value: 'AB Rh-' },
        { label: '0 Rh+', value: '0 Rh+' },
        { label: '0 Rh-', value: '0 Rh-' }
    ];

    useEffect(() => {
        let mounted = true;
        const loadCatalog = async () => {
            try {
                const res = await apiClient('Report/catalog');
                if (mounted && Array.isArray(res)) {
                    const pdksList = res.filter(
                        (r) =>
                            r.category === 'PDKS Raporları' ||
                            (!['personnel_list', 'personal_info', 'legal_consent_report'].includes(
                                r.reportType
                            ) &&
                                r.category !== 'Sicil & İK Raporları')
                    );
                    setCatalogReports(pdksList);
                    if (pdksList.length > 0 && !pdksList.some((r) => r.reportType === selectedReport)) {
                        setSelectedReport(pdksList[0].reportType);
                    }
                    return;
                }
            } catch (_) {
                // Fallback
            }

            if (mounted && permissions) {
                const allowed = FALLBACK_REPORTS.filter((r) => permissions.includes(r.permissionKey));
                setCatalogReports(allowed);
                if (!selectedReport && allowed.length > 0) {
                    setSelectedReport(allowed[0].reportType);
                }
            }
        };
        loadCatalog();
        return () => {
            mounted = false;
        };
    }, [permissions]);

    useEffect(() => {
        const fetchOptions = async () => {
            if (!firmCode) return;
            try {
                const [deps, poses, emps, locs, regs] = await Promise.all([
                    apiClient('Department'),
                    apiClient('Position'),
                    apiClient('Employee/dropdown'),
                    apiClient('Location').catch(() => []),
                    apiClient('WorkingRegime').catch(() => [])
                ]);
                setDepartments((deps || []).map((d) => ({ label: d.name, value: d.id })));
                setPositions((poses || []).map((p) => ({ label: p.name, value: p.id })));
                setPersonnel((emps || []).map((e) => ({ label: e.fullName, value: e.id })));
                setLocations((locs || []).map((l) => ({ label: l.name, value: l.id })));
                setWorkingRegimes((regs || []).map((w) => ({ label: w.name, value: w.id })));
            } catch (err) {
                console.error('Filtre verileri alınamadı:', err);
            }
        };
        fetchOptions();
    }, [firmCode]);

    const quickReports = useMemo(() => {
        const list = [];
        for (const key of QUICK_REPORT_KEYS) {
            const found = catalogReports.find((r) => r.reportType === key);
            if (found) list.push(found);
        }
        return list;
    }, [catalogReports]);

    const otherReports = useMemo(() => {
        return catalogReports.filter((r) => !QUICK_REPORT_KEYS.includes(r.reportType));
    }, [catalogReports]);

    const isOtherReportSelected = useMemo(() => {
        return otherReports.some((r) => r.reportType === selectedReport);
    }, [otherReports, selectedReport]);

    const activeReportMeta = useMemo(
        () => catalogReports.find((r) => r.reportType === selectedReport) || null,
        [catalogReports, selectedReport]
    );

    const activeSupportsViewModes = useMemo(() => {
        if (!activeReportMeta) return false;
        return (
            activeReportMeta.supportsViewModes === true ||
            VIEW_MODE_SUPPORTED_REPORTS.has(activeReportMeta.reportType)
        );
    }, [activeReportMeta]);

    const buildFiltersObject = (overrideViewMode, overrideSubtotals, overrideColorize) => ({
        startDate: startDate,
        endDate: endDate,
        departmentIds: (selectedDepartments || []).map((d) => d.value),
        positionIds: (selectedPositions || []).map((p) => p.value),
        personnelIds: (selectedPersonnel || []).map((p) => p.value),
        locationIds: (selectedLocations || []).map((l) => l.value),
        workingRegimeIds: (selectedWorkingRegimes || []).map((w) => w.value),
        genders: (selectedGenders || []).map((g) => g.value),
        bloodTypes: (selectedBloodTypes || []).map((b) => b.value),
        searchText: searchText || null,
        viewMode: overrideViewMode !== undefined ? overrideViewMode : viewMode,
        includeSubtotals: overrideSubtotals !== undefined ? overrideSubtotals : includeSubtotals,
        colorizeMatrix: overrideColorize !== undefined ? overrideColorize : colorizeMatrix
    });

    const handleGenerateReport = () => {
        if (!selectedReport) return;
        setReportRequest({
            reportType: selectedReport,
            filters: buildFiltersObject(),
            key: Date.now()
        });
    };

    const handleSelectReport = (repType) => {
        setSelectedReport(repType);
        const repMeta = catalogReports.find((r) => r.reportType === repType);
        const nextViewMode = repMeta?.defaultViewMode || viewMode || 'detailed';
        if (repMeta?.defaultViewMode) {
            setViewMode(repMeta.defaultViewMode);
        }
        if (reportRequest) {
            setReportRequest({
                reportType: repType,
                filters: buildFiltersObject(nextViewMode, includeSubtotals),
                key: Date.now()
            });
        }
    };

    const handleChangeViewMode = (newMode) => {
        setViewMode(newMode);
        if (reportRequest && selectedReport) {
            setReportRequest({
                reportType: selectedReport,
                filters: buildFiltersObject(newMode, includeSubtotals),
                key: Date.now()
            });
        }
    };

    const handleToggleSubtotals = (nextVal) => {
        setIncludeSubtotals(nextVal);
        if (reportRequest && selectedReport) {
            setReportRequest({
                reportType: selectedReport,
                filters: buildFiltersObject(viewMode, nextVal, colorizeMatrix),
                key: Date.now()
            });
        }
    };

    const handleToggleColorizeMatrix = (nextColorize) => {
        setColorizeMatrix(nextColorize);
        if (reportRequest && selectedReport) {
            setReportRequest({
                reportType: selectedReport,
                filters: buildFiltersObject(viewMode, includeSubtotals, nextColorize),
                key: Date.now()
            });
        }
    };

    const handleSavePreset = () => {
        const cleanName = presetNameInput.trim();
        if (!cleanName || !selectedReport) return;
        const newPreset = {
            id: `preset_${Date.now()}`,
            name: cleanName,
            reportType: selectedReport,
            viewMode,
            includeSubtotals,
            colorizeMatrix,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
            searchText,
            selectedDepartments,
            selectedPositions,
            selectedPersonnel,
            selectedLocations,
            selectedWorkingRegimes,
            selectedGenders,
            selectedBloodTypes
        };
        const nextList = [newPreset, ...savedPresets.filter((p) => p.name !== cleanName)].slice(0, 12);
        setSavedPresets(nextList);
        try {
            localStorage.setItem(presetStorageKey, JSON.stringify(nextList));
        } catch {}
        setPresetNameInput('');
        setShowPresetInput(false);
    };

    const handleApplyPreset = (preset) => {
        if (!preset) return;
        const nextRep = preset.reportType || selectedReport;
        const nextMode = preset.viewMode || 'detailed';
        const nextSubs = preset.includeSubtotals !== undefined ? preset.includeSubtotals : true;
        const nextCol = preset.colorizeMatrix !== undefined ? preset.colorizeMatrix : true;
        const sDate = preset.startDate ? new Date(preset.startDate) : startDate;
        const eDate = preset.endDate ? new Date(preset.endDate) : endDate;

        setSelectedReport(nextRep);
        setViewMode(nextMode);
        setIncludeSubtotals(nextSubs);
        setColorizeMatrix(nextCol);
        setStartDate(sDate);
        setEndDate(eDate);
        setSearchText(preset.searchText || '');
        setSelectedDepartments(preset.selectedDepartments || []);
        setSelectedPositions(preset.selectedPositions || []);
        setSelectedPersonnel(preset.selectedPersonnel || []);
        setSelectedLocations(preset.selectedLocations || []);
        setSelectedWorkingRegimes(preset.selectedWorkingRegimes || []);
        setSelectedGenders(preset.selectedGenders || []);
        setSelectedBloodTypes(preset.selectedBloodTypes || []);

        setReportRequest({
            reportType: nextRep,
            filters: {
                startDate: sDate,
                endDate: eDate,
                departmentIds: (preset.selectedDepartments || []).map((d) => d.value),
                positionIds: (preset.selectedPositions || []).map((p) => p.value),
                personnelIds: (preset.selectedPersonnel || []).map((p) => p.value),
                locationIds: (preset.selectedLocations || []).map((l) => l.value),
                workingRegimeIds: (preset.selectedWorkingRegimes || []).map((w) => w.value),
                genders: (preset.selectedGenders || []).map((g) => g.value),
                bloodTypes: (preset.selectedBloodTypes || []).map((b) => b.value),
                searchText: preset.searchText || null,
                viewMode: nextMode,
                includeSubtotals: nextSubs,
                colorizeMatrix: nextCol
            },
            key: Date.now()
        });
    };

    const handleDeletePreset = (id, e) => {
        e?.stopPropagation?.();
        const nextList = savedPresets.filter((p) => p.id !== id);
        setSavedPresets(nextList);
        try {
            localStorage.setItem(presetStorageKey, JSON.stringify(nextList));
        } catch {}
    };

    const handleReset = () => {
        const firstDay = new Date();
        firstDay.setDate(1);
        firstDay.setHours(0, 0, 0, 0);
        setStartDate(firstDay);
        setEndDate(new Date());
        setSearchText('');
        setSelectedDepartments([]);
        setSelectedPositions([]);
        setSelectedPersonnel([]);
        setSelectedLocations([]);
        setSelectedWorkingRegimes([]);
        setSelectedGenders([]);
        setSelectedBloodTypes([]);
        setReportRequest(null);
    };

    const setQuickDate = (type) => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

        switch (type) {
            case 'today':
                break;
            case 'yesterday':
                start.setDate(today.getDate() - 1);
                end.setDate(today.getDate() - 1);
                break;
            case 'thisWeek': {
                const day = today.getDay() || 7;
                start.setDate(today.getDate() - day + 1);
                break;
            }
            case 'lastWeek': {
                const lastWeekDay = today.getDay() || 7;
                start.setDate(today.getDate() - lastWeekDay - 6);
                end.setDate(today.getDate() - lastWeekDay);
                break;
            }
            case 'thisMonth':
                start.setDate(1);
                break;
            case 'lastMonth':
                start.setMonth(today.getMonth() - 1);
                start.setDate(1);
                end.setDate(0);
                break;
            default:
                break;
        }
        setStartDate(start);
        setEndDate(end);
    };

    const QuickDateBtn = ({ label, type }) => (
        <button
            type="button"
            onClick={() => setQuickDate(type)}
            className="px-3 py-1.5 text-xs font-semibold border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-emerald-500 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-lg transition-all duration-150"
        >
            {label}
        </button>
    );

    const FilterLabel = ({ children }) => (
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            {children}
        </label>
    );

    return (
        <div className="w-full space-y-6">
            <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700 space-y-6">
                {/* ÜST BAŞLIK */}
                <div className="flex flex-wrap justify-between items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2.5">
                            <FileText className="w-6 h-6 text-emerald-500" />
                            PDKS ve Puantaj Raporları
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Görüntülemek veya dışa aktarmak istediğiniz raporu seçin ve filtre kriterlerini belirleyin.
                        </p>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                        {catalogReports.length} Rapor Türü
                    </span>
                </div>

                {/* FAVORİ RAPOR & FİLTRE ŞABLONLARI BARI */}
                <div className="flex flex-wrap items-center justify-between gap-2.5 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 mr-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
                            Favori Raporlarım:
                        </span>
                        {savedPresets.length === 0 ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Sık kullandığınız rapor, görünüm modu (Detay / Tek Satır Özet) ve filtre seçimlerinizi kaydedip tek tıkla çalıştırabilirsiniz.
                            </span>
                        ) : (
                            savedPresets.map((preset) => (
                                <div
                                    key={preset.id}
                                    onClick={() => handleApplyPreset(preset)}
                                    className="group inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 hover:border-emerald-500 dark:hover:border-emerald-500 text-gray-800 dark:text-gray-200 cursor-pointer shadow-2xs transition-all"
                                    title="Bu favori raporu yükle ve çalıştır"
                                >
                                    <Star className="w-3 h-3 text-amber-500 fill-amber-400 shrink-0" />
                                    <span>{preset.name}</span>
                                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-semibold">
                                        {preset.viewMode === 'summary' ? 'Tek Satır Özet' : 'Detay'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDeletePreset(preset.id, e)}
                                        className="ml-0.5 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400"
                                        title="Favoriyi Sil"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {showPresetInput ? (
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="text"
                                    value={presetNameInput}
                                    onChange={(e) => setPresetNameInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                                    placeholder="Örn: Ekim Puantaj Tek Satır İcmal..."
                                    className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 w-56"
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={handleSavePreset}
                                    className="px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                    Kaydet
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPresetInput(false);
                                        setPresetNameInput('');
                                    }}
                                    className="px-2 py-1 rounded-lg text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                >
                                    İptal
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    const defaultLabel = activeReportMeta
                                        ? `${activeReportMeta.title} (${viewMode === 'summary' ? 'Özet' : 'Detay'})`
                                        : 'Favori Raporum';
                                    setPresetNameInput(defaultLabel);
                                    setShowPresetInput(true);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700 transition-all"
                            >
                                <BookmarkPlus className="w-3.5 h-3.5" />
                                Mevcut Seçimi Favorilere Ekle
                            </button>
                        )}
                    </div>
                </div>

                {/* 1. RAPOR SEÇİM ALANI: 5 HIZLI ERİŞİM BUTONU + DİĞER RAPORLAR SEÇİM LİSTESİ */}
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            1. Görüntülenecek Rapor Türünü Seçin
                        </div>
                        {otherReports.length > 0 && (
                            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                Hızlı erişim butonlarından veya sağdaki açılır listeden rapor seçebilirsiniz
                            </span>
                        )}
                    </div>

                    {catalogReports.length === 0 ? (
                        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
                            Hesabınıza tanımlı görüntülenebilir PDKS raporu bulunmamaktadır.
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
                                {/* Hızlı Seçim Butonları (Puantaj, Geç Gelen, Erken Çıkan, Gelmeyen, Ham Geçiş) */}
                                {quickReports.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5 flex-1">
                                        {quickReports.map((rep) => {
                                            const style =
                                                REPORT_STYLE_MAP[rep.reportType] ||
                                                REPORT_STYLE_MAP.puantaj_raporu;
                                            const Icon = style.icon;
                                            const isSelected = selectedReport === rep.reportType;
                                            const shortLabel =
                                                QUICK_BUTTON_LABELS[rep.reportType] || rep.title;

                                            return (
                                                <button
                                                    key={rep.reportType}
                                                    type="button"
                                                    onClick={() => handleSelectReport(rep.reportType)}
                                                    className={`group relative rounded-xl px-3.5 py-2.5 border transition-all duration-150 flex items-center gap-2.5 text-left ${
                                                        isSelected
                                                            ? 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-500 ring-1 ring-emerald-500/40 shadow-sm'
                                                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                                                    }`}
                                                >
                                                    <div
                                                        className={`w-8 h-8 rounded-lg ${style.iconBg} flex items-center justify-center shadow-xs shrink-0`}
                                                    >
                                                        <Icon className="w-4 h-4" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-xs font-bold text-gray-900 dark:text-white truncate">
                                                            {shortLabel}
                                                        </div>
                                                        <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                                            {isSelected ? 'Aktif Rapor' : 'Hızlı Seçim'}
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Diğer Raporlar Açılır Seçim Listesi */}
                                {otherReports.length > 0 && (
                                    <div className="lg:w-80 xl:w-96 shrink-0">
                                        <div
                                            className={`relative rounded-xl border transition-all duration-150 ${
                                                isOtherReportSelected
                                                    ? 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-500 ring-1 ring-emerald-500/40 shadow-sm'
                                                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                                            }`}
                                        >
                                            <div className="px-3 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center justify-between">
                                                <span>Diğer PDKS Raporları ({otherReports.length})</span>
                                                {isOtherReportSelected && (
                                                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                                        <CheckCircle2 className="w-3 h-3" /> Listeden Seçili
                                                    </span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <select
                                                    value={isOtherReportSelected ? selectedReport : ''}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleSelectReport(e.target.value);
                                                        }
                                                    }}
                                                    className="w-full appearance-none bg-transparent pl-3 pr-9 pb-2 pt-0.5 text-xs font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
                                                >
                                                    <option
                                                        value=""
                                                        className="bg-white dark:bg-gray-900 text-gray-500"
                                                    >
                                                        Listeden diğer bir rapor seçin...
                                                    </option>
                                                    {otherReports.map((rep) => (
                                                        <option
                                                            key={rep.reportType}
                                                            value={rep.reportType}
                                                            className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white py-1"
                                                        >
                                                            {rep.title}
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Seçili Raporun Kısa Bilgi Barı + GÖRÜNÜM MODU (DETAY / TEK SATIR ÖZET) SEÇİCİSİ */}
                            {activeReportMeta && (
                                <div className="space-y-2.5">
                                    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700">
                                        <div className="flex items-center gap-3 min-w-0">
                                            {(() => {
                                                const activeStyle =
                                                    REPORT_STYLE_MAP[activeReportMeta.reportType] ||
                                                    REPORT_STYLE_MAP.puantaj_raporu;
                                                const ActiveIcon = activeStyle.icon;
                                                return (
                                                    <div
                                                        className={`w-8 h-8 rounded-lg ${activeStyle.iconBg} flex items-center justify-center shrink-0`}
                                                    >
                                                        <ActiveIcon className="w-4 h-4" />
                                                    </div>
                                                );
                                            })()}
                                            <div className="min-w-0">
                                                <div className="text-xs font-bold text-gray-900 dark:text-white">
                                                    {activeReportMeta.title}
                                                </div>
                                                <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                                                    {activeReportMeta.description}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Görünüm Modu (Gün Bazlı Detay vs Personel Bazlı Tek Satır Özet) & Alt Toplam */}
                                        {activeSupportsViewModes ? (
                                            <div className="flex flex-wrap items-center gap-2.5 shrink-0 pt-2 xl:pt-0 border-t xl:border-t-0 border-gray-200 dark:border-gray-700">
                                                <div className="inline-flex items-center rounded-xl p-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 shadow-2xs">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleChangeViewMode('detailed')}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                            viewMode === 'detailed'
                                                                ? 'bg-emerald-600 text-white shadow-xs'
                                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        <Layers className="w-3.5 h-3.5" />
                                                        Gün / Hareket Bazlı Detay
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleChangeViewMode('summary')}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                            viewMode === 'summary'
                                                                ? 'bg-emerald-600 text-white shadow-xs'
                                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        <Table2 className="w-3.5 h-3.5" />
                                                        Personel Bazlı Tek Satır Özet (İcmal)
                                                    </button>
                                                </div>

                                                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-700 dark:text-gray-200 cursor-pointer select-none">
                                                    <input
                                                        type="checkbox"
                                                        checked={includeSubtotals}
                                                        onChange={(e) => handleToggleSubtotals(e.target.checked)}
                                                        className="rounded text-emerald-600 focus:ring-emerald-500 w-3.5 h-3.5"
                                                    />
                                                    <span>∑ Alt Toplam Satırı Göster</span>
                                                </label>
                                            </div>
                                        ) : activeReportMeta.reportType === 'monthly_attendance_matrix' ? (
                                            <div className="flex flex-wrap items-center gap-2.5 shrink-0 pt-2 xl:pt-0 border-t xl:border-t-0 border-gray-200 dark:border-gray-700">
                                                <div className="inline-flex items-center rounded-xl p-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 shadow-2xs">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleColorizeMatrix(false)}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                            !colorizeMatrix
                                                                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900 shadow-xs'
                                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        <span>Sade Çizelge (Renksiz)</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleColorizeMatrix(true)}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                            colorizeMatrix
                                                                ? 'bg-emerald-600 text-white shadow-xs'
                                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        <span>Renkli Vurgulu (FM / Devamsız / İzin)</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-100/80 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 shrink-0 self-start xl:self-center">
                                                Seçili Şablon
                                            </span>
                                        )}
                                    </div>

                                    {/* Aylık Yatay Puantaj / İmza Çizelgesi Sembol Rehberi */}
                                    {activeReportMeta.reportType === 'monthly_attendance_matrix' && (
                                        <div className="flex flex-wrap items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs">
                                            <span className="font-bold text-emerald-800 dark:text-emerald-300 mr-1">
                                                Çizelge Sembol Anahtarı:
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 font-bold">
                                                X : Normal Çalıştı
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-teal-100 dark:bg-teal-900/60 text-teal-800 dark:text-teal-200 font-bold">
                                                X+FM : Fazla Mesaili Çalıştı
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 font-bold">
                                                X(G) : Geç Gelerek Çalıştı
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold">
                                                HT / HT+ : Hafta Tatili / Tatilde Mesai
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold">
                                                RT / RT+ : Resmi Tatil / Resmi Tatilde Mesai
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/60 text-orange-800 dark:text-orange-200 font-bold">
                                                Yİ / RP / Üİ : Yıllık İzin / Rapor / Ücretsiz İzin
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-rose-100 dark:bg-rose-900/60 text-rose-800 dark:text-rose-200 font-bold">
                                                D : Devamsız (Gelmedi)
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* 2. FİLTRELEME PARAMETRELERİ */}
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-8">
                        {/* Tarih Kolonu */}
                        <div className="space-y-4">
                            <h3 className="flex items-center text-xs font-extrabold text-gray-800 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-2">
                                <div className="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mr-2">
                                    <Calendar className="w-3.5 h-3.5" />
                                </div>
                                Tarih Aralığı
                            </h3>

                            <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-3.5">
                                <div>
                                    <FilterLabel>Başlangıç Tarihi</FilterLabel>
                                    <CustomDatePicker
                                        selectedDate={startDate}
                                        onDateChange={setStartDate}
                                        showTime={false}
                                    />
                                </div>
                                <div>
                                    <FilterLabel>Bitiş Tarihi</FilterLabel>
                                    <CustomDatePicker
                                        selectedDate={endDate}
                                        onDateChange={setEndDate}
                                        showTime={false}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-1.5">
                                <QuickDateBtn label="Bugün" type="today" />
                                <QuickDateBtn label="Dün" type="yesterday" />
                                <QuickDateBtn label="Bu Hafta" type="thisWeek" />
                                <QuickDateBtn label="Geçen Hafta" type="lastWeek" />
                                <QuickDateBtn label="Bu Ay" type="thisMonth" />
                                <QuickDateBtn label="Geçen Ay" type="lastMonth" />
                            </div>
                        </div>

                        {/* Kimlik & Arama Kolonu */}
                        <div className="space-y-4">
                            <h3 className="flex items-center text-xs font-extrabold text-gray-800 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-2">
                                <div className="w-6 h-6 rounded-lg bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center mr-2">
                                    <Search className="w-3.5 h-3.5" />
                                </div>
                                Personel & Kimlik
                            </h3>

                            <div>
                                <FilterLabel>Serbest Arama (Ad, Sicil, T.C.)</FilterLabel>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleGenerateReport()}
                                        placeholder="Ad, Soyad, Sicil No veya T.C."
                                        className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white text-sm outline-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <FilterLabel>Personel Seçimi</FilterLabel>
                                <DarkAwareSelect
                                    options={personnel}
                                    value={selectedPersonnel}
                                    onChange={setSelectedPersonnel}
                                    isMulti
                                    placeholder="Tüm Personeller"
                                />
                            </div>
                        </div>

                        {/* Kurumsal Kolonu */}
                        <div className="space-y-4">
                            <h3 className="flex items-center text-xs font-extrabold text-gray-800 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-2">
                                <div className="w-6 h-6 rounded-lg bg-teal-100 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 flex items-center justify-center mr-2">
                                    <Building className="w-3.5 h-3.5" />
                                </div>
                                Kurumsal Yapı
                            </h3>
                            <div>
                                <FilterLabel>Departman(lar)</FilterLabel>
                                <DarkAwareSelect
                                    options={departments}
                                    value={selectedDepartments}
                                    onChange={setSelectedDepartments}
                                    isMulti
                                    placeholder="Tüm Departmanlar"
                                />
                            </div>
                            <div>
                                <FilterLabel>Pozisyon(lar)</FilterLabel>
                                <DarkAwareSelect
                                    options={positions}
                                    value={selectedPositions}
                                    onChange={setSelectedPositions}
                                    isMulti
                                    placeholder="Tüm Pozisyonlar"
                                />
                            </div>
                            <div>
                                <FilterLabel>Lokasyon(lar)</FilterLabel>
                                <DarkAwareSelect
                                    options={locations}
                                    value={selectedLocations}
                                    onChange={setSelectedLocations}
                                    isMulti
                                    placeholder="Tüm Lokasyonlar"
                                />
                            </div>
                        </div>

                        {/* İK Detay Kolonu */}
                        <div className="space-y-4">
                            <h3 className="flex items-center text-xs font-extrabold text-gray-800 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-2">
                                <div className="w-6 h-6 rounded-lg bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center mr-2">
                                    <HeartPulse className="w-3.5 h-3.5" />
                                </div>
                                İK & Demografi
                            </h3>
                            <div>
                                <FilterLabel>Çalışma Rejimi</FilterLabel>
                                <DarkAwareSelect
                                    options={workingRegimes}
                                    value={selectedWorkingRegimes}
                                    onChange={setSelectedWorkingRegimes}
                                    isMulti
                                    placeholder="Tümü"
                                />
                            </div>
                            <div>
                                <FilterLabel>Cinsiyet</FilterLabel>
                                <DarkAwareSelect
                                    options={genderOptions}
                                    value={selectedGenders}
                                    onChange={setSelectedGenders}
                                    isMulti
                                    placeholder="Tümü"
                                />
                            </div>
                            <div>
                                <FilterLabel>Kan Grubu</FilterLabel>
                                <DarkAwareSelect
                                    options={bloodTypeOptions}
                                    value={selectedBloodTypes}
                                    onChange={setSelectedBloodTypes}
                                    isMulti
                                    placeholder="Tümü"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Alt Aksiyon Barı */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <Info className="w-4 h-4 mr-1.5 text-emerald-500 shrink-0" />
                            {activeReportMeta
                                ? `"${activeReportMeta.title}" raporu seçili kriterlere göre hazırlanacaktır.`
                                : 'Rapor oluşturmak için yukarıdan bir rapor kartı seçiniz.'}
                        </div>
                        <div className="flex w-full sm:w-auto gap-3">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="flex-1 sm:flex-none inline-flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl shadow-sm hover:bg-gray-100 transition-all text-sm font-semibold"
                            >
                                <RotateCcw className="w-4 h-4 mr-1.5" />
                                Filtreleri Sıfırla
                            </button>
                            <button
                                type="button"
                                onClick={handleGenerateReport}
                                disabled={!selectedReport}
                                className={`flex-1 sm:flex-none px-7 py-2.5 rounded-xl shadow-sm transition-all text-sm font-bold flex items-center justify-center ${
                                    !selectedReport
                                        ? 'bg-gray-400 text-white cursor-not-allowed opacity-70'
                                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                }`}
                            >
                                <Filter className="w-4 h-4 mr-2" />
                                Raporu Oluştur
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. RAPOR SONUÇ TABLOSU */}
            {reportRequest && (
                <ReportRenderer
                    request={reportRequest}
                    hideDateBadge={activeReportMeta?.requiresDateRange === false}
                />
            )}
        </div>
    );
};

export default ReportsPage;
